import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useForm, Controller } from "react-hook-form";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Switch from "@mui/material/Switch";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

ProviderConfigDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  initial: PropTypes.shape({
    providerType: PropTypes.string,
    paymentMethodType: PropTypes.string,
    enabled: PropTypes.bool,
    config: PropTypes.object,
    supportedCurrencies: PropTypes.string,
    id: PropTypes.string,  // present when editing an existing config
  }),
};

ProviderConfigDialog.defaultProps = {
  initial: null,
};

const METHOD_TYPES = ["CARD", "WALLET", "BANK_SETTLEMENT"];
const CURRENCIES = ["USD", "ZiG", "ZWL"];

const FIELDS_BY_PROVIDER = {
  IVERI: [
    { key: "domain",         label: "Domain" },
    { key: "applicationId",  label: "Application ID" },
    { key: "certificateId",  label: "Certificate ID",  sensitive: true },
    { key: "merchantId",     label: "Merchant ID" },
    { key: "mode",           label: "Mode",            options: ["Test", "Live"] },
  ],
  IVERI_ECOCASH: [
    { key: "domain",         label: "Domain" },
    { key: "applicationId",  label: "Application ID" },
    { key: "certificateId",  label: "Certificate ID",  sensitive: true },
    { key: "merchantId",     label: "Merchant ID" },
    { key: "mode",           label: "Mode",            options: ["Test", "Live"] },
  ],
  SWITCH: [
    { key: "domain",  label: "Domain" },
    { key: "apiKey",  label: "API Key", sensitive: true },
  ],
  ECOCASH: [
    { key: "host",              label: "Host URL" },
    { key: "userName",          label: "Username" },
    { key: "password",          label: "Password",             sensitive: true },
    { key: "merchantCode",      label: "Merchant Code" },
    { key: "merchantPin",       label: "Merchant PIN",         sensitive: true },
    { key: "terminalId",        label: "Terminal ID" },
    { key: "notifyUrl",         label: "Notification URL" },
    { key: "superMerchantName", label: "Super Merchant Name" },
    { key: "merchantNumber",    label: "Merchant Number" },
  ],
  ONEMONEY: [
    { key: "host",               label: "Host URL" },
    { key: "thirdPartyID",       label: "Third Party ID" },
    { key: "receiverIdentifier", label: "Receiver Identifier" },
    { key: "password",           label: "Password",            sensitive: true },
    { key: "securityCredential", label: "Security Credential", sensitive: true },
  ],
  CBZ_SETTLEMENT: [
    { key: "domain",           label: "Domain" },
    { key: "username",         label: "Username" },
    { key: "password",         label: "Password",       sensitive: true },
    { key: "institutionId",    label: "Institution ID" },
    { key: "bulkSettlementApi",label: "Settlement API Path" },
    { key: "key",              label: "Encryption Key",  sensitive: true },
    { key: "mode",             label: "Mode",            options: ["test", "live"] },
  ],
  OTHER: [{ key: "notes", label: "Notes / Config" }],
};

export default function ProviderConfigDialog({ open, onClose, onSave, initial }) {
  const isEditing = !!initial?.id;
  const lockedProviderType = initial?.providerType || null;

  const [selectedCurrencies, setSelectedCurrencies] = useState([]);

  const { control, handleSubmit, reset, watch } = useForm({
    defaultValues: { paymentMethodType: "CARD", enabled: true },
  });

  const providerType = lockedProviderType || "IVERI";
  const fields = FIELDS_BY_PROVIDER[providerType] || [];

  const toggleCurrency = (currency) => {
    setSelectedCurrencies((prev) =>
      prev.includes(currency) ? prev.filter((c) => c !== currency) : [...prev, currency]
    );
  };

  useEffect(() => {
    if (!open) return;
    if (initial) {
      const defaults = {
        paymentMethodType: initial.paymentMethodType || "CARD",
        enabled: initial.enabled !== false,
      };
      // Pre-fill config field values
      const configFields = {};
      (FIELDS_BY_PROVIDER[initial.providerType] || []).forEach((f) => {
        configFields[`cfg_${f.key}`] = initial.config?.[f.key] || "";
      });
      reset({ ...defaults, ...configFields });
      // Pre-fill currency checkboxes
      if (initial.supportedCurrencies) {
        setSelectedCurrencies(initial.supportedCurrencies.split(",").map((c) => c.trim()));
      } else {
        setSelectedCurrencies([]);
      }
    } else {
      reset({ paymentMethodType: "CARD", enabled: true });
      setSelectedCurrencies([]);
    }
  }, [initial, open]);

  const onSubmit = (data) => {
    const config = {};
    fields.forEach((f) => {
      if (data[`cfg_${f.key}`] !== undefined && data[`cfg_${f.key}`] !== "") {
        config[f.key] = data[`cfg_${f.key}`];
      }
    });
    onSave({
      providerType,
      paymentMethodType: data.paymentMethodType,
      enabled: data.enabled,
      config,
      supportedCurrencies: selectedCurrencies.length > 0 ? selectedCurrencies.join(",") : null,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEditing ? `Edit ${providerType} Config` : `Configure ${providerType}`}
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Grid container spacing={2} mt={0}>
            {/* Provider type — locked, display only */}
            <Grid item xs={12} md={6}>
              <MDInput
                label="Provider Type"
                value={providerType}
                fullWidth
                disabled
                helperText="Provider type is fixed per route — one config per provider"
              />
            </Grid>

            {/* Payment method type */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="method-type-label">Payment Method Type *</InputLabel>
                <Controller
                  name="paymentMethodType"
                  control={control}
                  render={({ field }) => (
                    <Select {...field} labelId="method-type-label" label="Payment Method Type *">
                      <MenuItem value="">- Select Method -</MenuItem>
                      {METHOD_TYPES.map((m) => (
                        <MenuItem key={m} value={m}>{m}</MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
            </Grid>

            {/* Dynamic provider-specific fields */}
            {fields.map((f) => (
              <Grid item xs={12} md={6} key={f.key}>
                {f.options ? (
                  /* Select field (e.g. mode: Test / Live) */
                  <FormControl fullWidth>
                    <InputLabel id={`cfg-${f.key}-label`}>{f.label}</InputLabel>
                    <Controller
                      name={`cfg_${f.key}`}
                      control={control}
                      defaultValue=""
                      render={({ field: fld }) => (
                        <Select {...fld} labelId={`cfg-${f.key}-label`} label={f.label}>
                          <MenuItem value="">- Select {f.label} -</MenuItem>
                          {f.options.map((opt) => (
                            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                  </FormControl>
                ) : (
                  /* Text / password field */
                  <Controller
                    name={`cfg_${f.key}`}
                    control={control}
                    defaultValue=""
                    render={({ field: fld }) => (
                      <MDInput
                        {...fld}
                        label={`${f.label}${f.sensitive ? " (sensitive)" : ""}${["domain","applicationId","merchantId","merchantCode","terminalId","host","thirdPartyID","receiverIdentifier","username","institutionId","key"].includes(f.key) ? " *" : ""}`}
                        type={f.sensitive ? "password" : "text"}
                        fullWidth
                        autoComplete={f.sensitive ? "new-password" : undefined}
                        placeholder={f.sensitive && isEditing ? "Leave blank to keep existing value" : undefined}
                      />
                    )}
                  />
                )}
              </Grid>
            ))}

            {/* Supported currencies */}
            <Grid item xs={12}>
              <MDTypography variant="caption" fontWeight="medium" color="text">
                Supported Currencies
              </MDTypography>
              <MDTypography variant="caption" color="text" display="block" mb={0.5}>
                Leave all unchecked to support all currencies.
              </MDTypography>
              <FormGroup row>
                {CURRENCIES.map((c) => (
                  <FormControlLabel
                    key={c}
                    control={
                      <Checkbox
                        checked={selectedCurrencies.includes(c)}
                        onChange={() => toggleCurrency(c)}
                      />
                    }
                    label={c}
                  />
                ))}
              </FormGroup>
            </Grid>

            {/* Enabled toggle */}
            <Grid item xs={12}>
              <Controller
                name="enabled"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label="Enabled"
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <MDButton variant="outlined" color="secondary" onClick={onClose}>Cancel</MDButton>
          <MDButton type="submit" variant="gradient" color="info">
            {isEditing ? "Update" : "Save Config"}
          </MDButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}
